import {
  FolderTreeItem,
  useFolderActionPermissions,
  useSplitTreatments,
} from "@zapier/client";
import {
  KebabMenuItem,
  useEmitFolderActionEvent,
  FolderActionInteractionOption,
  FolderModal,
} from "@zapier/shared";
import { useMemo } from "react";
import {
  FolderTypeQueryState,
  FOLDER_TRASH_ID,
  isPersonalFolder,
} from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";

interface Input {
  folder?: FolderTreeItem;
  folderLevel: number;
  setCurrentModal: (modal: FolderModal | undefined) => void;
  setCurrentFolder: (folder: FolderTreeItem | undefined) => void;
}

export function useFolderActionItems({
  folder,
  folderLevel,
  setCurrentFolder,
  setCurrentModal,
}: Input) {
  const { emit } = useEmitFolderActionEvent();
  const { isDuplicateAssetsEnabled } = useSplitTreatments();
  const tAction = useTranslations("buttons");
  const showCopyToAnotherAccountAction =
    isDuplicateAssetsEnabled && folder?.id !== FOLDER_TRASH_ID;
  const isCopyToAnotherAccountDisabled = isPersonalFolder(folder?.id);
  const {
    canMove,
    canRename,
    disabledDeleteMessage,
    disabledMoveMessage,
    disabledShareMessage,
    canCreateSubfolder,
    canCreateSubfolderLimit,
    subfolderLimitMessage,
  } = useFolderActionPermissions({
    folderId: folder?.id,
    folderDepth: folderLevel + 1, // Pass child's depth to check if creation is allowed
  });

  const disabledCreateSubfolderMessage =
    canCreateSubfolder && !canCreateSubfolderLimit
      ? subfolderLimitMessage
      : undefined;

  return useMemo(() => {
    return [
      {
        icon: "actionEdit",
        label: "Rename",
        disabled: !canRename,
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Rename);
        },
      },
      {
        icon: "personAdd",
        label: "Share",
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Share);
        },
        disabled: !!disabledShareMessage,
        tooltipText: disabledShareMessage,
      },
      canCreateSubfolder
        ? {
            icon: "navFolder",
            label: "Create subfolder",
            disabled: canCreateSubfolder && !canCreateSubfolderLimit,
            tooltipText: disabledCreateSubfolderMessage,
            onClick: () => {
              emit({
                optionSelected: FolderActionInteractionOption.CreateSubfolder,
                interactionRawText: folder?.isShared
                  ? FolderTypeQueryState.Shared
                  : FolderTypeQueryState.Personal,
              });
              setCurrentFolder(folder);
              setCurrentModal(FolderModal.Create);
            },
          }
        : undefined,
      {
        icon: "actionMoveToFolder",
        label: tAction("move-to-folder"),
        disabled: !canMove,
        tooltipText: disabledMoveMessage || undefined,
        onClick: () => {
          emit({
            optionSelected: FolderActionInteractionOption.MoveFolder,
          });
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Move);
        },
      },
      showCopyToAnotherAccountAction ? { divider: true } : undefined,
      showCopyToAnotherAccountAction
        ? {
            icon: "arrowExit",
            label: tAction("copy-to-another-account"),
            disabled: isCopyToAnotherAccountDisabled,
            onClick: () => {
              setCurrentFolder(folder);
              setCurrentModal(FolderModal.CopyToAnotherAccount);
            },
          }
        : undefined,
      { divider: true },
      {
        icon: "actionTrash",
        label: tAction("delete"),
        onClick: () => {
          setCurrentFolder(folder);
          setCurrentModal(FolderModal.Delete);
        },
        disabled: Boolean(disabledDeleteMessage),
        hasWarning: true,
        tooltipText: disabledDeleteMessage,
      },
    ].filter(Boolean) as KebabMenuItem[];
  }, [
    folder,
    canCreateSubfolder,
    canCreateSubfolderLimit,
    disabledCreateSubfolderMessage,
    canMove,
    canRename,
    disabledDeleteMessage,
    disabledMoveMessage,
    disabledShareMessage,
    showCopyToAnotherAccountAction,
    isCopyToAnotherAccountDisabled,
    emit,
    setCurrentFolder,
    setCurrentModal,
    tAction,
  ]);
}
